import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import config from 'cityads-v3/config';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import { collectAllValues, arrayIncludesValue } from 'cityads-v3/utils/arrayUtils';

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import FilterBox from './FilterBox';
import IconButton from '@mui/material/IconButton'
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined'
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'
import { List as InfiniteList } from 'react-virtualized';
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText';

import classes from './FilterCheck.scss';

import TextField from 'cityads-v3/components/TextField/TextField';

const GUTTER_DEFAULT_OFFSET = 16;
const GUTTER_STEP = 12;
const ITEM_HEIGHT = 40;
const DEFAULT_VISIBLE_ITEMS = 8;

@withTranslation(['main'], { i18n, wait: !!window })
export default class FilterCheck extends Component {

  static propTypes = {
    items: PropTypes.array.isRequired,
    title: PropTypes.string,
    search: PropTypes.bool,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    onSearch: PropTypes.func,
    withInfinite: PropTypes.bool
  };

  static defaultProps = {
    value: [],
    search: false,
    onSearch: null,
    withInfinite: true,
  };

  state = {
    search: false
  }

  onChangeItem(id, set, evt) {
    let { value } = this.props;
    if (set) {
      value = [
        ...value,
        ...(id instanceof Array ? id : [id])
      ];
    } else {
      value = value.filter(item => id instanceof Array ? id.indexOf(item) === -1 : item !== id);
    }

    this.props.onChange(value, evt);
  }

  onSelectAll = set => () => {
    const { items, onChange } = this.props;
    if (set) {
      const value = items.map(item => item.id);
      onChange(value);
    } else {
      onChange([]);
    }
  }

  _onSearchTimer = 0

  onSearch = evt => {
    const { value } = evt.target;
    const { onSearch } = this.props;

    this.setState({
      search: value || false
    });

    if (typeof onSearch === 'function') {

      clearTimeout(this._onSearchTimer);
      this._onSearchTimer = setTimeout(() => {
        onSearch(value);
      }, config.KEYBOARD_TIMEOUT);

    }

  }

  renderSearch() {
    const { search, t, isLoading } = this.props;
    if (!search) return;

    return (
      <ListItem dense>
        <ListItemIcon classes={{ root: classes.searchIcon }}>
          {isLoading ?
            <CircularProgress size={24}/>
            :
            <SearchOutlinedIcon />
          }
        </ListItemIcon>
        <TextField
          placeholder={t('search')}
          onChange={this.onSearch}
          fullWidth={true}
        />
      </ListItem>
    );
  }

  renderCheckAllButtons() {
    const { onSearch, t } = this.props;
    return this.state.search && typeof onSearch === 'function' && (
      <ListItem dense classes={{root: classes.checkAllButtonContainer}}>
        <Button fullWidth size="small" variant="outlined" onClick={this.onSelectAll(true)}>{t('buttons.selectall')}</Button>
        <Button fullWidth size="small" variant="outlined" onClick={this.onSelectAll(false)}>{t('buttons.deselectall')}</Button>
      </ListItem>
    );
  }

  render() {
    const { title, value, onSearch, t, withInfinite, ...others } = this.props;
    const { search } = this.state;
    let { items } = this.props;

    if (search) {
      items = typeof onSearch === 'function' ? items : items.filter(item => item.name.toLowerCase().indexOf(search.toLowerCase()) !== -1);
    }

    const list = items.map(item => (
      <FilterCheckItem
        key={item.id}
        checked={item.id instanceof Array ? arrayIncludesValue(value, item.id) : value.indexOf(item.id) !== -1}
        onChange={(name, value, evt) => this.onChangeItem(name, value, evt)}
        value={value}
        {...item}
      />
    ));

    return (
      <FilterBox
        title={title}
        className={classes.filterBox}
        {...others}
      >
        {this.renderSearch()}

        {withInfinite ? (
          <InfiniteList
            className={classes.infiniteList}
            height={ITEM_HEIGHT * (items.length <= DEFAULT_VISIBLE_ITEMS ? items.length : DEFAULT_VISIBLE_ITEMS)}
            width={320}
            rowHeight={ITEM_HEIGHT}
            rowCount={items.length}
            rowRenderer={({index, key, style}) => (
              <div
                key={key}
                style={style}
              >
                {list[index]}
              </div>
            )}
          />
        ) : (
          list
        )}

        {this.renderCheckAllButtons()}

      </FilterBox>
    );
  }

}

export class FilterCheckItem extends Component {
  static propTypes = {
    checked: PropTypes.bool,
    count: PropTypes.string,
    disabled: PropTypes.bool,
    gutterLevel: PropTypes.number,
    icon: PropTypes.string,
    isIndeterminate: PropTypes.bool,
    items: PropTypes.array,
    name: PropTypes.string,
    onChange: PropTypes.func,
  }

  state = {
    expanded: false
  }

  toggleExpanded = evt => {
    evt && evt.stopPropagation();
    this.setState({
      expanded: !this.state.expanded
    });
  }

  renderExpander() {
    const { items } = this.props;

    if (!items || !items.length) {
      return false;
    }

    return (
      <IconButton classes={{ root: classes.expanderButton }} onClick={this.toggleExpanded} size="large">
        {this.state.expanded ? <ExpandLessOutlinedIcon /> : <ExpandMoreOutlinedIcon /> }
      </IconButton>
    );
  }

  renderChildren() {
    const { items, gutterLevel, value, ...others } = this.props;
    const { expanded } = this.state;

    if (!expanded || !items || !items.length) {
      return false;
    }

    return items.map(item => (
      <FilterCheckItem
        key={item.id}
        gutterLevel={gutterLevel ? gutterLevel + 1 : 1}
        {...others}
        {...item}
        checked={item.id instanceof Array ? arrayIncludesValue(value, item.id) : value.indexOf(item.id) !== -1}
        value={value}
      />
    ))
  }

  render() {
    const {
      id,
      checked,
      color,
      count,
      disabled,
      gutterLevel,
      icon,
      isIndeterminate,
      items,
      name,
      onChange,
    } = this.props;

    const label = count === undefined ? name : (
      <div>{name} <span className={classes.checkboxCount}>({count})</span></div>
    );

    const value = [
      ...(id instanceof Array ? id : [id]),
      ...collectAllValues(items, {
        valueKey: 'id',
        itemsKey: 'items'
      })
    ];

    return (
      <>
        <ListItem
          button
          dense
          key={id}
          role={undefined}
          onClick={evt => {
            onChange(value, !checked, evt)
          }}
          style={{
            background: color,
            paddingLeft: GUTTER_DEFAULT_OFFSET + (gutterLevel || 0) * GUTTER_STEP
          }}
        >
          <Checkbox
            ref="checkbox"
            title={name}
            disabled={disabled}
            checked={checked}
            indeterminate={isIndeterminate}
            classes={{
              root: classNames(classes.checkboxRoot, { [classes.checkboxRootWithIcon]: icon })
            }}
            sx={theme => ({ color: theme.palette.text.primary })}
          />
          <ListItemText
            classes={{
              primary: classes.listItemText
            }}
          >
            {label}
          </ListItemText>

          {this.renderExpander()}

        </ListItem>

        {this.renderChildren()}
      </>
    );
  }
}
